import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { createCoreboundRenderer } from './createCoreboundRenderer';
import { getCoreDieAppearance } from './coreDieAppearance';
import { createCoreDieScene } from './coreDieScene';
import { paintDieFace } from './dieFaceSkin';
import { getLabelContext } from './labelTextures';
import { observeElementSize } from '../../utils/observeElementSize';
import './CoreAttackDiePreview.css';

const CoreAttackDiePreview = ({ intent, reduceMotion }) => {
  const mountRef = useRef(null);
  const [fallback, setFallback] = useState(false);
  const skin = getCoreDieAppearance(intent.phase);
  const signature = intent.faces.join(',');

  useEffect(() => {
    const mount = mountRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 30);
    camera.position.set(0, 0.4, 6.5);
    camera.lookAt(0, 0, 0);
    let renderer, effects, frame, stopResize;
    const textures = [];
    const dispose = () => {
      cancelAnimationFrame(frame);
      stopResize?.();
      effects?.dispose();
      scene.traverse((object) => {
        object.geometry?.dispose();
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach((material) => material?.dispose());
      });
      textures.forEach((texture) => texture.dispose());
      renderer?.dispose();
      renderer?.forceContextLoss?.();
      renderer?.domElement.remove();
    };
    try {
      if (!window.WebGLRenderingContext) throw new Error('WebGL unavailable');
      renderer = createCoreboundRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0, 0);
      mount.appendChild(renderer.domElement);
      const materials = signature.split(',').map((value) => {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 256;
        const context = getLabelContext(canvas);
        if (!context) throw new Error('Canvas unavailable');
        paintDieFace(context, 256, 256, value, skin.color, skin.dieType);
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        textures.push(texture);
        return new THREE.MeshPhysicalMaterial({
          map: texture,
          metalness: skin.metalness,
          roughness: skin.roughness,
          emissive: skin.emissive,
          emissiveIntensity: 0.5,
          clearcoat: 1,
        });
      });
      const die = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), materials);
      scene.add(die, new THREE.AmbientLight(0xffffff, 2));
      const light = new THREE.DirectionalLight(0xffffff, 5);
      light.position.set(3, 5, 4);
      scene.add(light);
      effects = createCoreDieScene(scene, die, 1);
      const started = performance.now();
      const draw = (now = started) => {
        const elapsed = reduceMotion ? 0 : (now - started) / 1000;
        die.rotation.set(-0.32 + elapsed * 0.19, 0.55 + elapsed * 0.32, 0.08);
        effects.update({ phase: skin.phase, elapsed, camera, reduceMotion });
        renderer.render(scene, camera);
        if (!reduceMotion) frame = requestAnimationFrame(draw);
      };
      stopResize = observeElementSize(mount, ({ width, height }) => {
        renderer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        if (reduceMotion) draw();
      });
      draw();
      setFallback(false);
    } catch {
      dispose();
      setFallback(true);
      return undefined;
    }
    return dispose;
  }, [signature, skin, reduceMotion]);

  return (
    <figure
      className={`coreboundAttackDiePreview ${
        reduceMotion ? 'is-reduced' : ''
      }`}
      aria-label={`${skin.name} attack die: ${intent.faces.join(', ')} HP`}
      data-core-die-phase={skin.phase}
      data-faces={signature}
      style={{ '--preview-die-color': skin.color }}
    >
      <div
        className="coreboundAttackDieViewport"
        ref={mountRef}
        aria-hidden="true"
      >
        {fallback && (
          <div className="coreboundAttackDieFallback">
            {intent.faces.map((face, index) => (
              <b key={index}>{face}</b>
            ))}
          </div>
        )}
      </div>
      <figcaption>
        <span>CORE DAMAGE DIE</span>
        {skin.name}
      </figcaption>
    </figure>
  );
};

export default CoreAttackDiePreview;
